// Reading an RF code — the one piece of arithmetic the whole feature rests on.
//
// ⚠️ A CODE IS TWO FIELDS: the top 24 bits are the TRANSMITTER (one physical
// sensor) and the last 8 bits are the EVENT. EV1527 / PT2262, and proved
// against the rows already on the live server:
//
//   426115593  00011001011001100000001000001001   9 = Close  (magnet near)
//   426115610  00011001011001100000001000011010  26 = Open   (magnet away)
//   426115625  00011001011001100000001000101001  41 = a third event
//              └──── transmitter 1664514 ──────┘└ event ┘
//
// All three are ONE sensor. Two were learned under receiver 6263226 and the
// third under 2471550 — which is why grouping by transmitter, not by receiver,
// is what makes a sensor a sensor. The backend's rfDevices.js splits the code
// the same way; keep the two in step.
//
// ⚠️ WHICH BYTE IS OPEN IS A HARDWARE FACT THAT WAS WRITTEN DOWN BACKWARDS.
// On this product #26 = OPEN (magnet away) and #9 = CLOSE (magnet near) —
// measured on the Safe Locker (1664514) and confirmed on "Door Sensor 1"
// (3344130), whose labels had been entered the other way round and told
// people "Open" every time the door shut (2026-10-05). This comment used to
// say the opposite, and the mistake spread from it. A label is still the
// only source of meaning — nothing derives it — so a reversed install is fixed
// with "Reverse open / close" on the sensor, never in code.

/** Top 24 bits — the sensor. */
export const transmitterOf = (code) => Math.floor(Number(code) / 256) || 0;

/** Last 8 bits — the event. */
export const eventByteOf = (code) => {
  const n = Number(code);
  return Number.isFinite(n) ? ((n % 256) + 256) % 256 : 0;
};

/** Do these two codes come from the same piece of hardware? */
export const sameTransmitter = (a, b) => !!transmitterOf(a) && transmitterOf(a) === transmitterOf(b);

/**
 * The receiver's announcement: `[1127089]RfV(426115610,00011001…)`.
 *
 * ⚠️ READ OFF THE PAYLOAD, NOT THE TOPIC. The same press is also POSTed to the
 * server by the receiver, so this is only for LEARNING LIVE on screen — the app
 * never acts on it. Acting would double every RF-driven switch, because the
 * server already publishes the mapped actions.
 *
 * -> { code, binary } or null when the payload is anything else.
 */
export function parseRfV(payload) {
  const m = /RfV\(\s*(\d+)\s*,\s*([01]*)\s*\)/i.exec(String(payload || ''));
  if (!m) return null;
  const code = Number(m[1]);
  if (!Number.isFinite(code) || code <= 0) return null;
  return { code, binary: m[2] || '' };
}

/** 32-bit binary, for showing a code the way the receiver reports it. */
export const binaryOf = (code) => {
  const n = Number(code);
  if (!Number.isFinite(n) || n < 0) return '';
  return n.toString(2).padStart(32, '0');
};

/**
 * Codes (RFREMOTE rows) grouped into sensors.
 *
 * Used where the server has not grouped them yet — the learn panel, and any
 * older row whose RfDeviceId is still 0.
 * -> [{ transmitterId, codes: [row] }], most codes first.
 */
export function groupByTransmitter(rows) {
  const map = new Map();
  (rows || []).forEach((r) => {
    const code = Number(r?.REMOTEVALUE ?? r?.code);
    const t = transmitterOf(code);
    if (!t) return;
    if (!map.has(t)) map.set(t, { transmitterId: t, codes: [] });
    map.get(t).codes.push(r);
  });
  return [...map.values()].sort((a, b) => b.codes.length - a.codes.length);
}

/**
 * ⚠️ A MISSED FRAME MEANS THE STATE IS A GUESS. A transmitter repeats a frame
 * 4-8 times per event and nothing acknowledges it; if the Close burst is lost,
 * the last thing heard is still Open. So a state is never shown bare — it is
 * shown with when it was heard, and this is the one place that decides what
 * "a while ago" means.
 */
export const RF_STALE_MS = 12 * 60 * 60 * 1000;

export const rfStateIsStale = (lastAtMs, now = Date.now()) =>
  lastAtMs != null && now - lastAtMs > RF_STALE_MS;

export default transmitterOf;
